// Blob cache: once an asset is fetched the presentation never touches the
// network again for it, so a flaky conference connection cannot cause buffering.
const cache = new Map();
const failed = new Set();

export const resolveSrc = (url) => cache.get(url) || url;
export const isCached = (url) => cache.has(url);

async function fetchBlob(url, timeoutMs) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(url, { signal: ctrl.signal, mode: 'cors' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    cache.set(url, URL.createObjectURL(blob));
    return true;
  } catch (e) {
    failed.add(url);
    return false;
  } finally {
    clearTimeout(timer);
  }
}

function loadImage(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
}

export async function preloadAssets({ videos = [], images = [] }, onProgress) {
  const jobs = [
    ...videos.filter((v) => !cache.has(v.src)).map((v) => ({ type: 'video', url: v.src, label: v.label })),
    ...videos.map((v) => ({ type: 'image', url: v.poster, label: `${v.label} poster` })),
    ...images.map((url) => ({ type: 'image', url, label: 'image' })),
  ];
  const total = jobs.length || 1;
  let done = 0;
  const report = (label) => { done += 1; onProgress?.({ done, total, label, pct: Math.round((done / total) * 100) }); };
  const queue = [...jobs];
  const worker = async () => {
    while (queue.length) {
      const job = queue.shift();
      if (job.type === 'video') await fetchBlob(job.url, 120000);
      else await loadImage(job.url);
      report(job.label);
    }
  };
  await Promise.all([worker(), worker()]);
  return { failed: [...failed] };
}
